import React, { useEffect } from "react";
import {
  Item,
  TabList,
  Tabs,
  TabPanels,
  View,
  Flex,
  Content,
} from "@adobe/react-spectrum";
import { MessagesComponent } from "./message_list/MessagesComponent";
import { useMessagesState } from "../../../../hooks/useMessagesState";
import { useMessageOperations } from "../../../../hooks/useMessageOperations";
import { useMessageNodeId } from "../../../../hooks/useMessageNodeId";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { useTranslation } from "react-i18next";
import styles from "./communication_list.module.css";

interface ICommunicationListProps {
  setShow: (show: boolean) => void;
  setSelectedRecordView: (
    recordView:
      | "todo"
      | "messages"
      | "sent"
      | "trash"
      | "corporate"
      | "concept"
      | ""
  ) => void;
  setShowMobileMenu: (show: boolean) => void;
  messageType:
    | "todo"
    | "messages"
    | "sent"
    | "trash"
    | "corporate"
    | "concept"
    | "";
}

const CommunicationList: React.FC<ICommunicationListProps> = ({
  setShow,
  setSelectedRecordView,
  setShowMobileMenu,
  messageType,
}) => {
  const { t } = useTranslation();
  const { store, messageService, mainService, accessService } = useDependency();
  const { state, refs, actions } = useMessagesState();

  // Use the custom hook for messageNodeId initialization
  useMessageNodeId({
    store,
    accessService,
    setMessageNodeId: actions.setMessageNodeId,
    dependencies: [messageType],
  });

  const { getMessageListCount } = useMessageOperations({
    store,
    messageService,
    mainService,
    messageType,
    refs,
    messageNodeId: state.messageNodeId,
  });

  useEffect(() => {
    (async () => {
      // Only get message count if messageNodeId is available (not 0 for concept type)
      if (messageType !== "concept" || state.messageNodeId !== 0) {
        actions.setTotalMessageListRec(await getMessageListCount(""));
      }
    })();
  }, [messageType, state.messageNodeId]);

  return (
    <>
      <View
        width={"100%"}
        position={"relative"}
        height={"100%"}
        overflow={"hidden"}
        UNSAFE_className={styles.header}
      >
        <View UNSAFE_className={styles.message_heading_text}>
          {t(messageType, { ns: "layout_components" })}
        </View>
        {setShow && (
          <Flex
            direction={"row"}
            width={"60px"}
            position={"absolute"}
            top={"20px"}
            left={"16px"}
            alignItems={"center"}
            justifyContent={"start"}
            gap={"8px"}
            UNSAFE_className={styles.back_btn_parent}
          >
            <i
              className={`${styles.back_btn} bi bi-chevron-left`}
              onClick={() => {
                setShow(true);
              }}
            >
              <View height={"18px"} UNSAFE_className={styles.buttons}>
                View
              </View>
            </i>
          </Flex>
        )}
        <Flex
          direction={"row"}
          position={"absolute"}
          bottom={"0px"}
          top={setShow === undefined ? "25px" : "100px"}
          left={"5px"}
          width={"100%"}
          alignItems={"start"}
          justifyContent={"start"}
          gap={"12px"}
          UNSAFE_style={{ color: "#728289" }}
        >
          <Tabs
            aria-label="Communication List"
            density="compact"
            disabledKeys={[
              "internal_messages",
              "external_messages",
              "ai_chat_messages",
            ]}
            UNSAFE_className={styles.tabs}
          >
            <TabList>
              <Item key="all_messages">
                <Flex
                  direction={"column"}
                  width={"50px"}
                  alignItems={"center"}
                  justifyContent={"start"}
                  gap={"8px"}
                >
                  <Flex
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={"6px"}
                  >
                    <View UNSAFE_className={styles.label}>All</View>
                    <Flex
                      direction={"column"}
                      width={"28px"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      UNSAFE_className={styles.counter_circle}
                      UNSAFE_style={{ backgroundColor: "#1976D2" }}
                    >
                      <Content UNSAFE_className={styles.label_counter}>
                        {" "}
                        {(state.totalMessageListRec || 0) > 0
                          ? state.totalMessageListRec
                          : "0"}
                      </Content>
                    </Flex>
                  </Flex>
                </Flex>
              </Item>
              <Item key="internal_messages">
                <Flex
                  direction={"column"}
                  width={"73px"}
                  alignItems={"center"}
                  justifyContent={"start"}
                  gap={"8px"}
                >
                  <Flex
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={"6px"}
                  >
                    <View UNSAFE_className={styles.label}>Internal</View>
                    <Flex
                      direction={"column"}
                      width={"28px"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      UNSAFE_className={styles.counter_circle}
                      UNSAFE_style={{ backgroundColor: "#F72B1C" }}
                    >
                      <Content UNSAFE_className={styles.label_counter}>
                        {" "}
                        {(state.totalMessageListRec || 0) > 0
                          ? state.totalMessageListRec
                          : "0"}
                      </Content>
                    </Flex>
                  </Flex>
                </Flex>
              </Item>
              <Item key="external_messages">
                <Flex
                  direction={"column"}
                  width={"73px"}
                  alignItems={"center"}
                  justifyContent={"start"}
                  gap={"8px"}
                >
                  <Flex
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={"6px"}
                  >
                    <View UNSAFE_className={styles.label}>External</View>
                    <Flex
                      direction={"column"}
                      width={"28px"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      UNSAFE_className={styles.counter_circle}
                      UNSAFE_style={{ backgroundColor: "#8BC34A" }}
                    >
                      <Content UNSAFE_className={styles.label_counter}>
                        30
                      </Content>
                    </Flex>
                  </Flex>
                </Flex>
              </Item>
              <Item key="ai_chat_messages">
                <Flex
                  direction={"column"}
                  width={"73px"}
                  alignItems={"center"}
                  justifyContent={"start"}
                  gap={"8px"}
                >
                  <Flex
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={"6px"}
                  >
                    <View UNSAFE_className={styles.label}>AI chat</View>
                    <Flex
                      direction={"column"}
                      width={"28px"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      UNSAFE_className={styles.counter_circle}
                      UNSAFE_style={{ backgroundColor: "#14AFF5" }}
                    >
                      <Content UNSAFE_className={styles.label_counter}>
                        40
                      </Content>
                    </Flex>
                  </Flex>
                </Flex>
              </Item>
            </TabList>
            <TabPanels>
              <Item key="all_messages">
                <MessagesComponent
                  setShowMobileMenu={setShowMobileMenu}
                  messageType={messageType}
                />
              </Item>
              <Item key="internal_messages">
                There is no internal messages.
              </Item>
              <Item key="external_messages">
                There is no external messages.
              </Item>
              <Item key="ai_chat_messages">There is no AI chat messages.</Item>
            </TabPanels>
          </Tabs>
        </Flex>
      </View>
    </>
  );
};

export default CommunicationList;
